import "@mdxeditor/editor/style.css";

import { useEffect, useRef } from "react";

import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CodeToggle,
  CreateLink,
  headingsPlugin,
  imagePlugin,
  InsertImage,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  Separator,
  tablePlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { TTMarkdownContent } from "../TTMarkdownContent";
import TTLinkPlugin from "./TTLinkPlugin";

interface TTMarkdownEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  className?: string;
  handleImageUpload?: (file: File) => Promise<string>;
  readOnly?: boolean;
}

export default function TTMarkdownEditor({
  value,
  onChange,
  handleImageUpload,
  className,
  readOnly,
}: TTMarkdownEditorProps) {
  const { t } = useTranslation(["mdxEditor"]);
  const editorRef = useRef<MDXEditorMethods | null>(null);
  const lastValueRef = useRef<string>("");

  useEffect(() => {
    if (!editorRef.current) return;

    if (value === lastValueRef.current) {
      return;
    }

    lastValueRef.current = value;
    editorRef.current.setMarkdown(value);
  }, [value]);

  if (readOnly) {
    return <TTMarkdownContent>{value}</TTMarkdownContent>;
  }

  return (
    <MDXEditor
      ref={editorRef}
      markdown={value}
      onChange={(md) => {
        lastValueRef.current = md;
        onChange(md);
      }}
      className={clsx("TT-markdown-editor", className)}
      contentEditableClassName="TT-markdown-editor-content"
      translation={(key, defaultValue, interpolations) => t(key, { ...interpolations, defaultValue })}
      spellCheck={false}
      plugins={[
        headingsPlugin({ allowedHeadingLevels: [1, 2, 3] }),
        listsPlugin(),
        linkPlugin(),
        quotePlugin(),
        markdownShortcutPlugin(),
        tablePlugin(),
        linkDialogPlugin({ LinkDialog: () => <TTLinkPlugin /> }),
        imagePlugin({ imageUploadHandler: handleImageUpload }),
        toolbarPlugin({
          toolbarContents: () => (
            <>
              <BlockTypeSelect />
              <Separator />
              <BoldItalicUnderlineToggles />
              <Separator />
              <CodeToggle />
              <CreateLink />
              <InsertImage />
              <InsertTable />
              <Separator />
              <ListsToggle />
              <Separator />
              <UndoRedo />
            </>
          ),
        }),
      ]}
    />
  );
}
